import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ShieldCheck, Search, Trash2, ChevronLeft, ChevronRight, RefreshCw, Filter, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme, themes } from '../ThemeProvider';
import { API_URL } from '../../lib/api-client';

export function SmsDispatchLogsAdmin() {
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'failed'>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  // Row Selection State
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    fetchLogs(page, search, limit, statusFilter);
  }, [page, limit, statusFilter]);

  const fetchLogs = async (
    currentPage = page,
    searchQuery = search,
    currentLimit = limit,
    currentStatus = statusFilter
  ) => {
    try {
      setLoading(true);
      setSelectedIds([]); // reset selection when fetching
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/sms-campaign/logs?page=${currentPage}&limit=${currentLimit}&search=${encodeURIComponent(searchQuery)}&status=${currentStatus}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error (${res.status})`);
      }
      const data = await res.json();
      if (data.success && data.data) {
        setLogs(data.data.logs || []);
        if (data.data.pagination) {
          setTotalPages(data.data.pagination.pages || 1);
          setTotalLogs(data.data.pagination.total || 0);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch SMS dispatch logs:', err);
      toast.error('Failed to load SMS dispatch logs: ' + (err?.message || 'Network error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLogs(1, search, limit, statusFilter);
  };

  const handleStatusFilterChange = (status: 'all' | 'sent' | 'failed') => {
    setStatusFilter(status);
    setPage(1);
  };

  // Checkbox Selection Logic
  const handleSelectAllOnPage = () => {
    const pageIds = logs.map(l => l._id);
    const allSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

    if (allSelected) {
      // Unselect page IDs
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      // Add page IDs
      const combined = Array.from(new Set([...selectedIds, ...pageIds]));
      setSelectedIds(combined);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const isAllPageSelected =
    logs.length > 0 && logs.every(log => selectedIds.includes(log._id));

  // Delete Individual Log
  const handleDeleteLog = async (id: string) => {
    if (!confirm('Are you sure you want to delete this SMS dispatch log entry?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/sms-campaign/logs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Log entry deleted successfully');
        fetchLogs(page, search, limit, statusFilter);
      } else {
        toast.error(data.message || 'Failed to delete log entry');
      }
    } catch (err) {
      console.error('Delete log error:', err);
      toast.error('Failed to delete log entry');
    }
  };

  // Bulk Delete Selected Logs
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} selected SMS dispatch log entries?`)) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/sms-campaign/logs/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ logIds: selectedIds })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Successfully deleted ${selectedIds.length} selected logs`);
        setSelectedIds([]);
        fetchLogs(page, search, limit, statusFilter);
      } else {
        toast.error(data.message || 'Failed to delete selected logs');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('Failed to delete selected logs');
    }
  };

  // Clear All Logs
  const handleClearAllLogs = async () => {
    if (!confirm('WARNING: Are you sure you want to clear ALL SMS dispatch logs? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/sms-campaign/logs/clear-all`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('All SMS dispatch logs cleared successfully');
        setPage(1);
        setSelectedIds([]);
        fetchLogs(1, '', limit, statusFilter);
      } else {
        toast.error(data.message || 'Failed to clear logs');
      }
    } catch (err) {
      console.error('Clear logs error:', err);
      toast.error('Failed to clear dispatch logs');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="text-base md:text-lg font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                Recent Global SMS Dispatch Logs
              </CardTitle>
              <CardDescription className="text-xs">
                Dedicated real-time tracking, status filtering, multi-selection, and single/bulk deletion ({totalLogs} total logs).
              </CardDescription>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Delete Selected Button */}
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white font-medium"
                >
                  <Trash2 className="h-4 w-4" /> Delete Selected ({selectedIds.length})
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(page, search, limit, statusFilter)}
                className="flex items-center gap-1"
              >
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>

              {logs.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAllLogs}
                  className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 text-red-500" /> Clear All Logs
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Filter Tabs & Search Bar */}
          <div className="flex flex-col md:flex-row gap-3 justify-between items-stretch md:items-center">
            {/* Status Filter Buttons */}
            <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-lg border text-xs font-medium">
              <button
                type="button"
                onClick={() => handleStatusFilterChange('all')}
                className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${
                  statusFilter === 'all'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Filter className="h-3.5 w-3.5" /> All Logs
              </button>
              <button
                type="button"
                onClick={() => handleStatusFilterChange('sent')}
                className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${
                  statusFilter === 'sent'
                    ? 'bg-emerald-600 text-white shadow-sm font-semibold'
                    : 'text-emerald-700 hover:bg-emerald-50'
                }`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Sent
              </button>
              <button
                type="button"
                onClick={() => handleStatusFilterChange('failed')}
                className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${
                  statusFilter === 'failed'
                    ? 'bg-red-600 text-white shadow-sm font-semibold'
                    : 'text-red-700 hover:bg-red-50'
                }`}
              >
                <XCircle className="h-3.5 w-3.5" /> Failed
              </button>
            </div>

            {/* Search Bar & Page Size Selector */}
            <div className="flex flex-col md:flex-row gap-2 flex-1 justify-end">
              <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search recipient, contact name, or email..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 text-sm"
                  />
                </div>
                <Button type="submit" style={{ backgroundColor: currentTheme.primary }} className="text-white">
                  Search
                </Button>
              </form>

              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span>Per page:</span>
                <select
                  value={limit}
                  onChange={e => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="p-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      onChange={handleSelectAllOnPage}
                      className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      title="Select all on this page"
                    />
                  </th>
                  <th className="p-3">Recipient Number</th>
                  <th className="p-3">Contact Name</th>
                  <th className="p-3">Sent By User</th>
                  <th className="p-3">Message Snippet / Error Details</th>
                  <th className="p-3">Sent At</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-500">
                      Loading dispatch logs...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-500">
                      No SMS dispatch logs found for this filter.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const isSelected = selectedIds.includes(log._id);
                    return (
                      <tr key={log._id} className={`hover:bg-gray-50 ${isSelected ? 'bg-purple-50/60' : ''}`}>
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectOne(log._id)}
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                          />
                        </td>
                        <td className="p-3 font-mono font-semibold text-gray-900">{log.recipientNumber}</td>
                        <td className="p-3">{log.contactName || '-'}</td>
                        <td className="p-3">{log.senderEmail || log.senderUserId?.email || 'App User'}</td>
                        <td className="p-3 max-w-xs truncate text-xs text-gray-500" title={log.errorMessage || log.messageText}>
                          {log.status === 'failed' && log.errorMessage ? (
                            <span className="text-red-600 font-medium">❌ {log.errorMessage}</span>
                          ) : (
                            log.messageText || '-'
                          )}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">
                          {new Date(log.sentAt).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <Badge className={log.status === 'sent' ? 'bg-green-500' : 'bg-red-500'}>
                            {log.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLog(log._id)}
                            title="Delete this entry"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 h-auto"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-gray-500">
              Showing Page {page} of {totalPages} ({totalLogs} total entries
              {selectedIds.length > 0 && `, ${selectedIds.length} selected`})
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                className="flex items-center gap-1 text-xs"
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>

              <div className="flex items-center gap-1 text-xs px-2 font-medium">
                {page} / {totalPages}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                className="flex items-center gap-1 text-xs"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default SmsDispatchLogsAdmin;
